import { connect, ConnectedProps } from "react-redux";
import { RootState } from "../store/store"; // Assuming your store file is named 'store.ts'
import {
  Button,
  Cascader,
  Col,
  DatePicker,
  Form,
  Image,
  Input,
  Popconfirm,
  Row,
  Select,
  Space,
  Switch,
} from "antd";
import {
  AppstoreOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  TableOutlined,
} from "@ant-design/icons";
import { deleteData, fetchData, postData, putData } from "../store/actions/cms";
import { useState } from "react";
import { PATH_EXPENSE as PATH, PATH_MANAGE, PATH_ORGANISER } from "../config";
import CustomPaginatedTable from "../components/CustomPaginatedTable";
import PageHeader from "../components/PageHeader";
import { queryClient } from "../store/queryClient";
import { useMutation, useQuery } from "@tanstack/react-query";
import { queryApi } from "../store/actions/common";
import SingleUpload from "../components/SingleUpload";
import { triggerNotification } from "../store/directActions";
import CustomDrawer from "../components/hoc/CustomDrawer";
import { useLocalState } from "../hooks/useLocalStorage";
import moment from "moment";

const { Option } = Select;

interface OwnProps {}

type configurationPageProps = OwnProps & PropsFromRedux;

const PAGE_TYPE = "expense";
const VIEW_MODE_STORAGE_KEY = "shared-page-view-mode";

const ConfigurationPage: React.FC<configurationPageProps> = ({ storeId }) => {
  const [showDrawer, setShowDrawer] = useState(false);
  const [viewMode, setViewMode] = useLocalState(VIEW_MODE_STORAGE_KEY, "card");

  const [filterData, setFilterData] = useState({ query: 3966 });
  const [amount, setAmount] = useState(0);

  const [form] = Form.useForm();

  const updateDrawer = (state: boolean = false) => {
    setShowDrawer(state);
  };

  const handleEditClicked = (record: any) => {
    form.resetFields();
    if (record) {
      const values = {
        ...record,
        close_date: record.close_date ? moment(record.close_date) : null,
        open_date: moment(record.open_date),
        // provider_id: values.provider.name
      };
      form.setFieldsValue({
        ...values,
        isFormEdit: true,
        uniId: values.lottery_id,
      });
      updateDrawer(true);
    }
  };

  const {
    data: organiserData,
    isPending,
    isError,
    error,
  } = useQuery({
    queryKey: [PATH_ORGANISER, { type: "all" }],
    // queryFn: ({ signal, queryKey }) => fetchEvents({ signal, ...queryKey[1] }),
    queryFn: () => queryApi(PATH_ORGANISER, "GET"),
    staleTime: Infinity,
    gcTime: 0,
  });

  const {
    mutate,
    isPending: isPendingMutation,
    // isError,
    // error,
  } = useMutation({
    mutationFn: ({ data, method, path }: any) => queryApi(path, method, data),
    onSuccess: () => {
      updateDrawer();
      queryClient.invalidateQueries({
        queryKey: [PATH],
      });
    },
    onError: (a) => {
      triggerNotification({
        title: "Error",
        description: a.message,
        type: "error",
      });
    },
  });

  const onFormSubmit = (values: Record<string, any>) => {
    const payload: Record<string, any> = {
      ...values,
      close_date: values.close_date
        ? values.close_date.format("YYYY-MM-DD")
        : values.close_date,
      open_date: values.open_date
        ? values.open_date.format("YYYY-MM-DD")
        : moment().format("YYYY-MM-DD"),
      size: values.size ? values.size : "regular",
      image_url: values?.image_url?.url
        ? values.image_url.url
        : values.image_url,
      top_prize: values.top_prize ?? 0,
    };

    if (values.isFormEdit) {
      const uniId = values.uniId;
      delete payload["uniId"];
      delete payload["isFormEdit"];
      mutate({
        path: `${PATH}/${uniId}`,
        method: "PUT",
        data: { ...payload, currency: "$", lottery_id: uniId },
      });
    } else {
      mutate({
        path: `${PATH}`,
        method: "POST",
        data: { size: "regular", ...payload, status: 1, currency: "$" },
      });
    }
  };

  const onUpdateItem = (record: any) => {
    mutate({
      path: `${PATH}/${record.lottery_id}`,
      method: "PUT",
      data: { ...record, status: !record.status },
    });
  };

  const quickItemUpdate = (item: any, update: Record<string, any>) => {
    mutate({
      path: `${PATH}/${item.lottery_id}`,
      method: "PUT",
      data: { ...item, ...update },
    });
  };

  const columns = [
    {
      title: "#",
      dataIndex: "game_number",
      key: "game_number",
      render: (item, record, index) => <div>{index + 1}</div>,
    },
    {
      title: "Image",
      dataIndex: "image_url",
      key: "image_url",
      render: (image_url) => (
        <Image
          width={60}
          height={60}
          src={image_url.replace("original", "original")}
        />
      ),
    },
    {
      title: "State",
      dataIndex: "provider",
      key: "provider",
      sorter: (a, b) =>
        (a.provider?.name ?? "").localeCompare(b.provider?.name ?? ""),
      render: (item, record, index) => <div>{item.name}</div>,
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => (a.name ?? "").localeCompare(b.name ?? ""),
    },
    {
      title: "Game #",
      dataIndex: "game_number",
      key: "game_number",
      sorter: (a, b) => (a.game_number ?? 0) - (b.game_number ?? 0),
    },
    {
      title: "Value",
      dataIndex: "amount",
      key: "amount",
      sorter: (a, b) => (a.amount ?? 0) - (b.amount ?? 0),
      render: (item, record, index) => (
        <div>
          {record.currency}
          {record.amount}
        </div>
      ),
    },
    {
      title: "Pack Size",
      dataIndex: "pack_size",
      key: "pack_size",
      sorter: (a, b) => (a.pack_size ?? 0) - (b.pack_size ?? 0),
    },
    // {
    //   title: "Open Date",
    //   dataIndex: "open_date",
    //   key: "open_date",
    // },
    {
      title: "Date Added",
      dataIndex: "created_at",
      key: "created_at",
      sorter: (a, b) =>
        moment(a.created_at).valueOf() - moment(b.created_at).valueOf(),
      render: (item, record, index) => (
        <div>{moment(item).format("MMMM DD, YYYY")}</div>
      ),
    },

    //   {
    //     title: "Close Date",
    //     dataIndex: "close_date",
    //     key: "top_pclose_daterize",
    //   },

    // {
    //   title: "Top Prize",
    //   dataIndex: "top_prize",
    //   key: "top_prize",
    //   render: (item, record, index) => (
    //     <div>
    //       {record.currency}
    //       {record.top_prize}
    //     </div>
    //   ),
    // },
    {
      title: "New Game",
      key: "is_new",
      render: (item, record, index) => (
        <Space size="middle">
          <Switch
            checked={item.is_new}
            onChange={() => quickItemUpdate(record, { is_new: !item.is_new })}
          />
        </Space>
      ),
    },

    {
      title: "Action",
      key: "action",
      render: (record) => (
        <Space size="middle">
          {/* <a onClick={() => onActionItem(record)}>Edit</a> */}

          <EditOutlined
            style={{ fontSize: 22 }}
            onClick={() => handleEditClicked(record)}
          />

          <Popconfirm
            title="Are you sure to delete this?"
            onConfirm={() => onUpdateItem(record)}
            onCancel={() => {}}
            okText="Yes"
            cancelText="No"
          >
            <DeleteOutlined style={{ fontSize: 22, color: "red" }} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const customRender = (item: any, index: number) => (
    <div
      key={item.lottery_id || index}
      className="app-surface rounded-xl shadow-sm border border-solid app-border p-6 hover:shadow-md transition-shadow duration-200 mb-4"
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-4 flex-1">
          {item.image_url && (
            <Image
              width={80}
              height={80}
              src={item.image_url?.replace("original", "original")}
              className="rounded-lg object-cover"
            />
          )}
          <div className="flex-1">
            <h3 className="text-lg font-semibold app-text mb-1">
              {item.name}
            </h3>
            <p className="text-sm app-muted">
              {item.provider?.name} • Game #{item.game_number}
            </p>
          </div>
        </div>
        <Space size="middle">
          <EditOutlined
            style={{ fontSize: 20 }}
            onClick={() => handleEditClicked(item)}
          />
          <Popconfirm
            title="Are you sure to delete this?"
            onConfirm={() => onUpdateItem(item)}
            onCancel={() => {}}
            okText="Yes"
            cancelText="No"
          >
            <DeleteOutlined style={{ fontSize: 20, color: "red" }} />
          </Popconfirm>
        </Space>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <p className="text-sm app-muted">Value</p>
          <p className="text-lg font-semibold"
            style={{ color: "var(--dash-value)" }}>
            {item.currency}
            {item.amount}
          </p>
        </div>
        <div>
          <p className="text-sm app-muted">Pack Size</p>
          <p className="text-lg font-semibold app-text">
            {item.pack_size}
          </p>
        </div>
      </div>

      <div className="flex justify-between items-center pt-4 border-0 border-t border-solid app-border">
        <span className="text-sm app-muted">
          {moment(item.created_at).format("MMMM DD, YYYY")}
        </span>
        <Switch
          checked={item.is_new}
          onChange={() => quickItemUpdate(item, { is_new: !item.is_new })}
        />
      </div>
    </div>
  );

  return (
    <section id="customers" className="page-section h-page p-6 flex flex-col">
      {/* Header and Actions */}
      {/* <div className="app-surface rounded-lg shadow-sm border app-border p-6 mb-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
          <div>
            <h2 className="text-2xl font-bold text-[#0c1248]">
              Expense Management
            </h2>
            <p className="app-muted mt-1">
              Track and manage all vehicle-related expenses
            </p>
          </div>
          <div className="flex items-center space-x-3 mt-4 md:mt-0">
            <button
              onClick={() => {
                form.resetFields();
                updateDrawer(true);
              }}
              className="px-4 py-2 bg-[#0c1248] text-white font-medium rounded-lg hover:bg-[#0c1248]/90 transition duration-200 flex items-center"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 mr-2"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
              Add Expense
            </button>
          </div>
        </div>
      </div> */}
      <PageHeader
        title="Manage Lottery"
        extra={[
          <Cascader
            key="filter"
            value={[[filterData.query, amount]]}
            style={{ width: 300 }}
            disabled={isPending}
            // @ts-ignore
            options={organiserData?.map((item) => {
              return {
                ...item,
                value: item.state_id,
                label: item.name,
                children: [
                  { value: 0, label: "All Values" },

                  { value: 1, label: "$1" },
                  { value: 2, label: "$2" },
                  { value: 3, label: "$3" },
                  { value: 5, label: "$5" },
                  { value: 7, label: "$7" },
                  { value: 10, label: "$10" },
                  { value: 15, label: "$15" },
                  { value: 20, label: "$20" },
                  { value: 25, label: "$25" },
                  { value: 30, label: "$30" },
                  { value: 50, label: "$50" },
                ],
              };
            })}
            onChange={(value) => {
              console.log(value);
              setFilterData((prev) => ({
                ...prev,
                query: value[0],
              }));
              setAmount(value[1]);
              // getLotterydata(value[0]);
            }}
            placeholder="All States"
          />,
          <Button
            key="add"
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              form.resetFields();
              updateDrawer(true);
            }}
          >
            Add
          </Button>,
          <Button.Group key="view-toggle">
            <Button
              type={viewMode === "table" ? "primary" : "default"}
              icon={<TableOutlined />}
              onClick={() => setViewMode("table")}
            >
              Table
            </Button>
            <Button
              type={viewMode === "card" ? "primary" : "default"}
              icon={<AppstoreOutlined />}
              onClick={() => setViewMode("card")}
            >
              Card
            </Button>
          </Button.Group>,
        ]}
      />

      {/* @ts-ignore */}
      <CustomPaginatedTable
        columns={columns}
        showLimitChanger
        filterData={filterData}
        localFilterFunction={(data) => {
          return (
            data?.filter((item) => item.amount === amount || amount === 0) ?? []
          );
        }}
        queryKey={[PATH, { type: "all" }]}
        path={`${PATH}`}
        customRender={viewMode === "card" ? customRender : null}
        customWrapperClass={
          viewMode === "card"
            ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            : undefined
        }
        tableProps={
          viewMode === "table"
            ? {
                sticky: true,
                tableLayout: "auto",
                scroll: { y: true, x: true },
              }
            : undefined
        }
      />

      <CustomDrawer
        title="Add New Lottery"
        width={600}
        onClose={() => updateDrawer()}
        open={showDrawer}
        maskClosable={false}
        bodyStyle={{ paddingBottom: 80 }}
        footer={
          <div
            style={{
              textAlign: "right",
            }}
          >
            <Button
              onClick={() => updateDrawer()}
              style={{ marginRight: 8 }}
              disabled={isPendingMutation}
            >
              Cancel
            </Button>
            <Button
              loading={isPendingMutation}
              onClick={() => {
                form.submit();
              }}
              type="primary"
            >
              Submit
            </Button>
          </div>
        }
      >
        <Form
          form={form}
          onFinish={onFormSubmit}
          layout="vertical"
          hideRequiredMark
        >
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item name="isFormEdit" hidden={true}></Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item name="uniId" hidden={true}></Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item
                name="provider_id"
                label="State"
                rules={[{ required: true, message: "Please select state " }]}
              >
                <Select
                // defaultValue={1}
                >
                  {organiserData?.map((d) => (
                    <Option value={d.state_id}>{d.name}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item
                name="name"
                label="Name"
                rules={[
                  { required: true, message: "Please enter lottery name" },
                ]}
              >
                <Input placeholder="eg. $3000,00 extra play" />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name="game_number"
                label="Game #"
                rules={[{ required: true, message: "Please enter game #" }]}
              >
                <Input placeholder="1234" />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name="pack_size"
                label="Pack Size"
                rules={[{ required: true, message: "Please enter pack size" }]}
              >
                <Input placeholder="30" />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name="amount"
                label="Value"
                rules={[{ required: true, message: "Please enter value" }]}
              >
                <Input placeholder="10" />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name="open_date"
                label="Open Date"
                // rules={[
                //   { required: true, message: "Please enter amount" },
                // ]}
              >
                <DatePicker
                  style={{ width: "100%" }}
                  // getPopupContainer={trigger => trigger.parentElement}
                  onChange={(date, dateString) => console.log(dateString)}
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name="close_date"
                label="Close Date"
                // rules={[
                //   { required: true, message: "Please enter top prize" },
                // ]}
              >
                <DatePicker
                  style={{ width: "100%" }}
                  getPopupContainer={(trigger) => trigger.parentElement}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="top_prize" label="Top Prize">
                <Input placeholder="1,00,000" />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name="image_url"
                label="Image"
                rules={[{ required: true, message: "Please add image" }]}
                // getValueFromEvent={uploadFile}
                // trigger={onFormSubmit}
              >
                <SingleUpload />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item name="size" label="Image Size" defaultValue="regular">
                <Select
                  defaultValue="regular"
                  placeholder="Please choose image size"
                >
                  <Option value="regular">Regular</Option>
                  <Option value="large">Large</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </CustomDrawer>

      {/* Expenses Table */}
      {/* <div className="app-surface rounded-lg shadow-sm border app-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="app-surface-alt border-b app-border">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium app-muted uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium app-muted uppercase tracking-wider">
                  Vehicle
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium app-muted uppercase tracking-wider">
                  Category
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium app-muted uppercase tracking-wider">
                  Description
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium app-muted uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium app-muted uppercase tracking-wider">
                  Receipt
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium app-muted uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="app-surface divide-y divide-gray-200">
              {expenses.map((expense) => (
                <tr key={expense.id} className="app-hover">
                  <td className="px-6 py-4 whitespace-nowrap text-sm app-text">
                    {new Date(expense.date).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm app-text">
                    <div className="flex items-center gap-2">
                      <Car className="w-4 h-4 app-muted" />
                      {expense.vehicle}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${getCategoryColor(
                        expense.category
                      )}`}
                    >
                      {getCategoryIcon(expense.category)}
                      {expense.category}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm app-text max-w-xs truncate">
                    {expense.description}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold app-text">
                    ${expense.amount.toFixed(2)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm app-muted">
                    {expense.receipt ? (
                      <span className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 cursor-pointer">
                        View Receipt
                      </span>
                    ) : (
                      <span className="app-muted">No receipt</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm app-muted">
                    <div className="flex items-center gap-2">
                      <button
                        // onClick={() => handleEdit(expense)}
                        className="text-blue-600 hover:text-blue-900 transition-colors"
                      >
                        <Icon name="Edit2" className="w-4 h-4" />
                      </button>
                      <button
                        // onClick={() => handleDelete(expense.id)}
                        className="text-red-600 hover:text-red-900 transition-colors"
                      >
                        <Icon name="Trash2" className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div> */}

      {/* Add/Edit Modal */}
      {/* {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="app-surface rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="p-6">
              <h3 className="text-lg font-semibold app-text mb-4">
                {editingExpense ? "Edit Expense" : "Add New Expense"}
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium app-text mb-1">
                    Date
                  </label>
                  <input
                    type="date"
                    required
                    className="w-full px-3 py-2 border app-border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={formData.date}
                    onChange={(e) =>
                      setFormData({ ...formData, date: e.target.value })
                    }
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium app-text mb-1">
                    Vehicle
                  </label>
                  <select
                    required
                    className="w-full px-3 py-2 border app-border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={formData.vehicle}
                    onChange={(e) =>
                      setFormData({ ...formData, vehicle: e.target.value })
                    }
                  >
                    <option value="">Select Vehicle</option>
                    {vehicles.map((vehicle) => (
                      <option key={vehicle} value={vehicle}>
                        {vehicle}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium app-text mb-1">
                    Category
                  </label>
                  <select
                    required
                    className="w-full px-3 py-2 border app-border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={formData.category}
                    onChange={(e) =>
                      setFormData({ ...formData, category: e.target.value })
                    }
                  >
                    <option value="">Select Category</option>
                    {categories.map((cat) => (
                      <option key={cat} value={cat}>
                        {cat}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium app-text mb-1">
                    Description
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="Enter expense description"
                    className="w-full px-3 py-2 border app-border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={formData.description}
                    onChange={(e) =>
                      setFormData({ ...formData, description: e.target.value })
                    }
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium app-text mb-1">
                    Amount ($)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    required
                    placeholder="0.00"
                    className="w-full px-3 py-2 border app-border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={formData.amount}
                    onChange={(e) =>
                      setFormData({ ...formData, amount: e.target.value })
                    }
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium app-text mb-1">
                    Receipt (optional)
                  </label>
                  <input
                    type="text"
                    placeholder="Receipt filename or reference"
                    className="w-full px-3 py-2 border app-border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={formData.receipt}
                    onChange={(e) =>
                      setFormData({ ...formData, receipt: e.target.value })
                    }
                  />
                </div>
                <div className="flex gap-3 pt-4">
                  <button
                    onClick={() => setShowAddModal(false)}
                    className="flex-1 px-4 py-2 app-text app-surface-alt border app-border rounded-lg app-hover transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSubmit}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    {editingExpense ? "Update" : "Add"} Expense
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )} */}
    </section>
  );
};

const mapStateToProps = (state: RootState) => ({
  // Map state properties to props here if needed
  data: state.cms?.[PAGE_TYPE],
  storeId: state.user?.profileData?.storeId,
  REFRESH: state.levent?.[PAGE_TYPE],
});

const mapDispatchToProps = (dispatch: any) => ({
  // login: (data: any) => dispatch(getProfile(data)),
  fetchCms: (reqData: any) =>
    dispatch(fetchData(PATH_MANAGE, PATH, reqData, PAGE_TYPE)),
  generateToken: (id: any) =>
    dispatch(fetchData(PATH_MANAGE, `${PATH}/login/app/${id}`, {}, "")),
  createData: (reqData: any) =>
    dispatch(postData(PATH_MANAGE, `${PATH}`, reqData, PAGE_TYPE)),

  editData: (id: string, reqData: any) =>
    dispatch(putData(PATH_MANAGE, `${PATH}/`, id, reqData, PAGE_TYPE)),
  removeCms: (reqData: any) =>
    dispatch(deleteData(PATH_MANAGE, `${PATH}/`, reqData, PAGE_TYPE)),
});

const connector = connect(mapStateToProps, mapDispatchToProps);

// Define the props that connect will inject into the component
type PropsFromRedux = ConnectedProps<typeof connector>;

// Connect the component to Redux
export default connector(ConfigurationPage);
